import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getVendorStats, getVendorTransactions } from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import { format } from 'date-fns';
import useToast from '../../hooks/useToast';
import { CurrencyRupeeIcon, UsersIcon, CalendarIcon } from '@heroicons/react/24/outline';

const VendorOverview = () => {
  const { user } = useAuth();
  const socket = useSocket();
  const [stats, setStats] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, txnRes] = await Promise.all([
        getVendorStats(),
        getVendorTransactions({ limit: 5 })
      ]);
      setStats(statsRes.data);
      setRecentTxns(txnRes.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handleUpdate = () => fetchData();
    socket.on('walletUpdated', handleUpdate);
    return () => socket.off('walletUpdated', handleUpdate);
  }, [socket]);

  if (loading) {
    return <div className="text-center py-10 text-[#708090]">Loading...</div>;
  }

  // --- STAT CARDS (Customized to your request) ---
  const statCards = [
    { 
      title: 'Total Collected', 
      value: `₹${stats?.totalCollected?.toFixed(2) || '0.00'}`, 
      icon: CurrencyRupeeIcon,
      description: 'Lifetime earnings'
    },
    { 
      title: "Today's Collection", 
      value: `₹${stats?.todayCollected?.toFixed(2) || '0.00'}`, 
      icon: CalendarIcon,
      description: 'Earned today'
    },
    { 
      title: 'Total Customers', 
      // Uses a Set to count unique users (Logic is explained below)
      value: stats?.uniqueCustomers || 0, 
      icon: UsersIcon,
      description: 'Unique attendees'
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-normal text-[#2b3445]">Vendor Overview</h1>
      <p className="text-sm text-[#708090]">Welcome back, {user?.name}</p>

      {/* Stats Section (3 Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card) => (
          <div key={card.title} className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">{card.title}</p>
                <p className="text-2xl font-bold text-[#2b3445] mt-1">{card.value}</p>
                <p className="text-xs text-[#708090] mt-1">{card.description}</p>
              </div>
              <div className="p-2 bg-[#f0f7ff] rounded-[8px]">
                <card.icon className="h-6 w-6 text-[#1471b9]" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Transactions Section (No QR Code) */}
      <div>
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-[#2b3445]">Recent Transactions</h2>
          </div>
          
          {recentTxns.length === 0 ? (
            <div className="text-center py-8 text-[#708090]">No recent transactions</div>
          ) : (
            <ul className="divide-y divide-[#dae1e7]">
              {recentTxns.map((t) => (
                <li key={t._id} className="py-3 flex justify-between items-center hover:bg-[#f8f9fa] px-2 rounded-[8px] transition-colors">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-[#2b3445]">{t.userId?.name || t.userId || 'Unknown'}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-[#708090]">{t.description || 'Purchase'}</p>
                      <span className="text-[10px] text-[#a0afbe]">•</span>
                      <p className="text-[10px] text-[#a0afbe]">
                        {t.createdAt ? format(new Date(t.createdAt), 'dd MMM, HH:mm') : 
                         (t._id ? format(new Date(parseInt(t._id.toString().slice(0, 8), 16) * 1000), 'dd MMM, HH:mm') : '')}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-green-600">+₹{t.amount.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorOverview;